import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useApi } from "@/lib/api";
import { useMe } from "@/lib/me";

// Owners confirm with their password (or an emailed code); everyone else sends the delete to an owner for approval.
export default function DeleteDialog({ item, onClose, onDone }) {
  const api = useApi();
  const owner = useMe().role === "owner";
  const [method, setMethod] = useState(null);
  const [secret, setSecret] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!item || !owner) return;
    setMethod(null); setSecret("");
    api("post", "/confirm/start").then((r) => {
      setMethod(r);
      if (r.method === "code") toast.info(`Code sent to ${r.sent_to}`);
    }).catch((e) => { toast.error(e.message); setMethod({ method: "error" }); });
  }, [item, owner, api]);

  if (!item) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { kind: item.kind, target: item.target, reason };
      if (owner) body[method?.method === "code" ? "code" : "password"] = secret;
      const r = await api("post", "/deletions", body);
      toast.success(r.status === "deleted" ? `Deleted ${r.label}` : "Sent to an owner for approval");
      window.dispatchEvent(new Event("deletions-changed"));
      onDone(r.status);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-teal-700" /> {owner ? "Confirm delete" : "Ask an owner to delete"}</DialogTitle>
            <DialogDescription data-testid="delete-dialog-label">{item.label}</DialogDescription>
          </DialogHeader>
          {owner ? (
            !method ? <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Checking…</p>
              : method.method === "error" ? <p className="text-sm text-rose-600">Can't confirm right now — try again.</p>
              : (
                <div className="space-y-1.5">
                  <p className="text-sm text-slate-600">{method.method === "code" ? `Enter the 6-digit code we emailed to ${method.sent_to}.` : "Type your password to confirm it's you."}</p>
                  <Input autoFocus required type={method.method === "code" ? "text" : "password"} inputMode={method.method === "code" ? "numeric" : undefined}
                    autoComplete={method.method === "code" ? "one-time-code" : "current-password"} value={secret} onChange={(e) => setSecret(e.target.value)}
                    placeholder={method.method === "code" ? "123456" : "Password"} data-testid="delete-confirm-input" />
                </div>
              )
          ) : (
            <div className="space-y-1.5">
              <p className="text-sm text-slate-600">It stays in place until an owner approves. Owners get an email.</p>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why should it be deleted? (e.g. uploaded twice)" data-testid="delete-reason-input" />
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="delete-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy || (owner && (!method || method.method === "error"))} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-submit-button">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />} {owner ? "Delete" : "Send for approval"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
